/**
 * Lite loading skeletons (mobile UX review, quick wins).
 *
 * Card-shaped placeholders that match the final layout's dimensions so
 * content doesn't jump when it lands. Replaces the raw spinners / '...'
 * strings that made loading feel stalled. Respects prefers-reduced-motion
 * via Tailwind's animate-pulse.
 */

const bone = 'rounded-md bg-slate-200 dark:bg-slate-700';

export function SkeletonSiteRow() {
  return (
    <div className="animate-pulse flex items-center gap-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-4 py-3">
      <div className={`${bone} h-2.5 w-2.5 rounded-full shrink-0`} />
      <div className="flex-1 min-w-0 space-y-2">
        <div className={`${bone} h-3.5 w-3/5`} />
        <div className={`${bone} h-2.5 w-2/5`} />
      </div>
      <div className={`${bone} h-5 w-14 rounded-full shrink-0`} />
    </div>
  );
}

export function SkeletonList({ rows = 4 }: { rows?: number }) {
  return (
    <div className="space-y-3" aria-hidden="true">
      {Array.from({ length: rows }, (_, i) => (
        <SkeletonSiteRow key={i} />
      ))}
    </div>
  );
}

/** Inline stat-number placeholder (dashboard counter cards). */
export function SkeletonStat() {
  return (
    <span
      className={`${bone} animate-pulse inline-block h-8 w-10 align-middle`}
      aria-hidden="true"
    />
  );
}

/** Text-line placeholders for list wells (journal, corrective actions). */
export function SkeletonLines({ rows = 3 }: { rows?: number }) {
  return (
    <div className="animate-pulse space-y-3 px-4 py-4" aria-hidden="true">
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="space-y-1.5">
          <div className={`${bone} h-3 w-4/5`} />
          <div className={`${bone} h-2.5 w-1/2`} />
        </div>
      ))}
    </div>
  );
}

/** Borderless skeleton rows for use INSIDE an existing card/table shell. */
export function SkeletonRows({ rows = 6 }: { rows?: number }) {
  return (
    <div
      className="divide-y divide-slate-100 dark:divide-slate-700"
      aria-hidden="true"
    >
      {Array.from({ length: rows }, (_, i) => (
        <div
          key={i}
          className="animate-pulse flex items-center gap-3 px-4 py-3.5"
        >
          <div className={`${bone} h-2.5 w-2.5 rounded-full shrink-0`} />
          <div className="flex-1 min-w-0 space-y-2">
            <div className={`${bone} h-3.5 w-2/5`} />
            <div className={`${bone} h-2.5 w-1/4`} />
          </div>
          <div
            className={`${bone} h-5 w-16 rounded-full shrink-0 hidden sm:block`}
          />
          <div className={`${bone} h-3 w-20 shrink-0 hidden md:block`} />
        </div>
      ))}
    </div>
  );
}

/** Dashboard-shaped page skeleton: stat tiles + a wide map card + widgets. */
export function SkeletonDashboard() {
  return (
    <div className="space-y-6 animate-pulse" aria-hidden="true">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {Array.from({ length: 4 }, (_, i) => (
          <div
            key={i}
            className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5 space-y-3"
          >
            <div className={`${bone} h-3 w-1/2`} />
            <div className={`${bone} h-8 w-16`} />
          </div>
        ))}
      </div>
      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5 space-y-4">
          <div className={`${bone} h-4 w-40`} />
          <div className={`${bone} h-72 w-full rounded-lg`} />
        </div>
        <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5 space-y-3">
          <div className={`${bone} h-4 w-32`} />
          {Array.from({ length: 5 }, (_, i) => (
            <div key={i} className={`${bone} h-10 w-full rounded-lg`} />
          ))}
        </div>
      </div>
    </div>
  );
}
